import React, { Component, type ReactNode } from 'react';
import { Provider, type StyleMarshalContextProps } from '../common/context';
import type { ClassName } from '../types';

type Props = {
	children: ReactNode;
	scope: string;
};

const toSelector = (className: ClassName): string => `.${String(className)}`;

// eslint-disable-next-line jira/react/no-class-components
export default class StyleMarshalProvider extends Component<Props> {
	static defaultProps = {
		scope: 'default',
	};

	constructor(props: Props) {
		super(props);
		this.styleElement = document.createElement('style');
		this.styleElement.type = 'text/css';
		this.styleElement.id = 'roadmap-data-styles';
		const head = document.querySelector('head');
		if (head) {
			head.appendChild(this.styleElement);
		}
	}

	componentWillUnmount() {
		const parent = this.styleElement.parentNode;

		if (parent) {
			parent.removeChild(this.styleElement);
		}
	}

	onInsertCssRule = (className: ClassName, rule: string) => {
		this.onRemoveCssRule(className);

		this.insertRule(toSelector(className), rule);
	};

	onRemoveCssRule = (className: ClassName) => {
		const ruleIndex = this.getRuleIndex(toSelector(className));
		if (ruleIndex !== -1) {
			this?.styleElement?.sheet?.deleteRule(ruleIndex);
		}
	};

	getRuleIndex(selector: string): number {
		return Array.from(this?.styleElement?.sheet?.cssRules ?? []).findIndex(
			// @ts-expect-error - TS2339 - Property 'selectorText' does not exist on type 'CSSRule'.
			(rule) => rule.selectorText === `.${this.props.scope} ${selector}`,
		);
	}

	insertRule(selector: string, rule: string) {
		this?.styleElement?.sheet?.insertRule(
			`.${this.props.scope} ${String(selector)} {${rule.replace('\n', ' ')}}`,
			this.styleElement.sheet.cssRules.length,
		);
	}

	styleElement: HTMLStyleElement;

	render() {
		const { children, scope } = this.props;

		return (
			<Provider
				value={
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					{
						insert: this.onInsertCssRule,
						remove: this.onRemoveCssRule,
					} as StyleMarshalContextProps
				}
			>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<div className={scope}>{children}</div>
			</Provider>
		);
	}
}
